import CustomModal from './CustomModal';
import { useEffect, useState } from 'react';
import { Dropzone } from './Dropzone';
import { FileWithPath } from 'react-dropzone';
import { DocumentCard } from './DocumentCard';
import showToast from './ToastComponent';
import { DosageDiaryUploaderProps } from '../interface/interface';
import { createTrainingToolProfile } from '../service/createTrainingToolProfile';
import Loading from './Loading';
import { FILE_LIMITS } from '../constant/constant';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export function DoctorTrainingToolUploader({
  token,
  setUploaderVisibility,
  isUploaderVisible,
  fetchTrigger,
}: DosageDiaryUploaderProps) {
  const [files, setFiles] = useState<Array<FileWithPath>>([]);
  const [profileName, setProfileName] = useState<string>();
  const [type, setType] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const qc = useQueryClient();

  const uploadedFiles = useMutation({
    mutationFn: async () => {
      setLoading(true);
      const result = await createTrainingToolProfile(token, {
        name: profileName!,
        file: files[0],
        type,
      });
      if (typeof result === 'number' && result === 201) {
        setFiles([]);
        setProfileName(undefined);
        showToast({
          type: 'success',
          message: 'Training Tool Profile Created',
        });
        setUploaderVisibility(false);
        fetchTrigger();
        qc.invalidateQueries({
          queryKey: ['uploadTools', profileName, files, type],
        });
        return;
      }
      showToast({
        type: 'info',
        message: result.toString(),
      });
    },
    mutationKey: ['uploadTools', profileName, files, type],
  });

  const handleClose = () => {
    setUploaderVisibility(false);
    setFiles([]);
    setType('');
  };

  useEffect(() => {
    if (!loading || !uploadedFiles.isPending) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [loading, uploadedFiles.isPending]);

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <CustomModal
        isOpen={isUploaderVisible}
        onClose={() => handleClose()}
        header="Create Training Tool Profile"
        disableBackdropClose
        footer={
          <button
            className={`text-base px-6 py-3 text-white rounded-md 
              ${profileName === '' || !files.length ? 'bg-secondary-cornflowerblue-400 cursor-not-allowed' : 'bg-secondary-cornflowerblue-600'}`}
            onClick={async () =>
              await uploadedFiles.mutateAsync().finally(() => setLoading(false))
            }
            disabled={profileName === '' || !files.length}
          >
            Upload
          </button>
        }
        modalSize="w-5/12"
      >
        <div className="sm:col-span-2 mt-2 mb-4">
          <label
            htmlFor="first-name"
            className="text-sm  text-secondary-pink-700"
          >
            Profile Name <span className="text-red-500">*</span>
          </label>
          <div className="mt-2">
            <input
              type="text"
              name="profileName"
              id="profile-name"
              className={`text-sm font-regular border  
                        rounded-md p-2 w-full 
                        ${
                          profileName === ''
                            ? 'border-red-400'
                            : 'border-secondary-cornflowerblue-400'
                        }`}
              value={profileName}
              onChange={(e) => setProfileName(e.target.value)}
            />
          </div>
          {profileName === '' && (
            <small className="text-red-600">Name is required</small>
          )}
        </div>
        <Dropzone
          setAcceptedFiles={(uploadedFiles) => {
            const filteredFiles = uploadedFiles.filter((file) => {
              const isPdf = FILE_LIMITS.pdf.mimeTypes.includes(file.type);
              const isVideo = FILE_LIMITS.video.mimeTypes.includes(file.type);

              setType(file.type);

              if (!isPdf && !isVideo) {
                showToast({
                  type: 'warning',
                  message: 'Only PDF and Video files are allowed.',
                });
                return false;
              }

              const maxSize = isPdf
                ? FILE_LIMITS.pdf.maxSize
                : FILE_LIMITS.video.maxSize;

              if (file.size > maxSize) {
                showToast({
                  type: 'warning',
                  message: isPdf
                    ? 'PDF size should not exceed 5MB.'
                    : 'Video size should not exceed 50MB.',
                });
                return false;
              }

              return true;
            });

            if (filteredFiles.length > 0) {
              setFiles(filteredFiles);
            }
          }}
          type="Doctor Training Tool Document (PDF max 5MB / Video max 50MB)"
        />
        <div className="py-2">
          <DocumentCard
            item={files.map((file) => ({
              name: file.name,
              size: (file.size / (1024 * 1024))?.toFixed(2) + ' MB',
              onDelete: () => setFiles([]),
            }))}
          />
        </div>
      </CustomModal>
    </>
  );
}
